"use client";

import { Box, Button, Center, Loader, Stack, Text } from "@mantine/core";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import clsx from "clsx";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

import CustomModal from "@/app/components/modals/CustomModal";
import { BookRounded } from "@mui/icons-material";
import { useCompletionCardStyles } from "@/app/(core)/profile/completion/Cards/SubTopicCompletionCard";
import { titleToUrl } from "@utils/routing";
import {
  useCustomTestStateSelector,
  useMockExamStateSelector,
} from "../../../../../../../../../lib/redux/hooks";
import { TestFormats } from "../Stepper/types";
import classes from "./NonAttemptedTestsModal.module.css";

interface NonAttemptedTestsModalProps {
  testFormat: TestFormats;
  opened: boolean;
  onClose: (() => void) | undefined;
}

interface NonAttemptedTest {
  documentId: string;
  TestId: string;
  Breakdown: {
    numQuestions: number;
    laMarks: number;
    mcMarks: number;
  };
  RelatedSubTopics: Array<{
    documentId: string;
    SubTopicNumber: string;
    SubTopicTitle: string;
    IsHigherLevel: boolean;
    OfficialTag: string;
  }>;
  RelatedSubject: {
    documentId: string;
    Name: string;
  };
}

export default function NonAttemptedTestsModal({
  testFormat,
  opened,
  onClose,
}: NonAttemptedTestsModalProps) {
  const [tests, setTests] = useState<NonAttemptedTest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const customTestState = useCustomTestStateSelector();
  const mockExamState = useMockExamStateSelector();
  const relevantState =
    testFormat === TestFormats.custom ? customTestState : mockExamState;

  const { classes: completionClasses } = useCompletionCardStyles({
    subjectColorIndex: "maths",
  });

  const fetchNonAttemptedTests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/strapi/retrieveNonAttemptedTests", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          testFormat,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.msg || "Failed to fetch tests");
      }
      const allTests = data.tests || [];
      const attemptedTestIds = new Set<string>();
      Object.values(relevantState).forEach((subjectTests) => {
        Object.keys(subjectTests ?? {}).forEach((testId) => {
          attemptedTestIds.add(testId);
        });
      });

      const nonAttemptedTests = allTests.filter(
        (test: NonAttemptedTest) => !attemptedTestIds.has(test.TestId)
      );

      setTests(nonAttemptedTests);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [relevantState, testFormat]);

  useEffect(() => {
    if (opened) {
      setTests([]);
      setError(null);
      fetchNonAttemptedTests();
    }
  }, [opened, fetchNonAttemptedTests]);

  return (
    <CustomModal
      size="lg"
      TitleIcon={BookRounded}
      title={testFormat === TestFormats.custom ? "Custom Tests" : "Mock Exams"}
      opened={opened}
      onClose={onClose || (() => {})}
    >
      {loading ? (
        <Center>
          <Loader type="dots" color="darkblue" />
        </Center>
      ) : error ? (
        <>
          <Text c="red">Error: {error}</Text>
          <Button onClick={fetchNonAttemptedTests} mt="md">
            Try Again
          </Button>
        </>
      ) : (
        <Stack gap="xl">
          {tests.length === 0 ? (
            <Text className={classes.noDataNotice}>
              You have no non-attempted{" "}
              {testFormat === TestFormats.custom
                ? "custom tests"
                : "mock exams"}
              . All your created tests have been attempted!
            </Text>
          ) : (
            tests.map((test) => {
              const subjectName = test.RelatedSubject.Name;
              return (
                <Box
                  key={test.documentId}
                  className={clsx(completionClasses.wrapper, classes.card)}
                >
                  <Box className={classes.contentColumn}>
                    <Text className={completionClasses.subtopicTitle}>
                      {subjectName}: {test.TestId}
                    </Text>
                    <Text className={classes.statsText}>
                      {test.Breakdown.numQuestions} questions · MC:{" "}
                      {test.Breakdown.mcMarks} · LA: {test.Breakdown.laMarks} ·{" "}
                      {test.RelatedSubTopics.length} subtopic
                      {test.RelatedSubTopics.length !== 1 ? "s" : ""}
                    </Text>
                  </Box>
                  <Button
                    component={Link}
                    href={`/ib/${titleToUrl(subjectName)}/test-taker?testId=${
                      test.TestId
                    }`}
                    color="darkblue"
                    size="xs"
                    className={completionClasses.continueButton}
                    leftSection={
                      <PlayArrowRounded style={{ width: 16, height: 16 }} />
                    }
                  >
                    Attempt
                  </Button>
                </Box>
              );
            })
          )}
        </Stack>
      )}
    </CustomModal>
  );
}
